import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, onSnapshot, doc, updateDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Loader } from '@googlemaps/js-api-loader';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
const theme = createTheme({ direction: 'rtl' });

function formatDateDMY(dateStr) {
    if (!dateStr) return '';
    const d = dayjs(dateStr);
    return d.isValid() ? d.format('DD/MM/YYYY') : dateStr;
}

function TripBookingBlock({ trip, onFieldChange, onCheckAvailability, isLoading, onCancel, isConfirmed, onUseCurrentLocation, onSelectSuggestion, onFetchSuggestions, currentLocation }) {
    const waiting = trip.waitingForApproval || { immediate: false, scheduled: false, recurring: false };
    return (
        <div className="mb-8">
            {isConfirmed ? (
                <div className="p-4 bg-gray-800 rounded-lg border border-green-500 shadow-lg">
                    <h4 className="text-lg font-bold mb-3 text-green-400">הזמנה מאושרת!</h4>
                    <div className="space-y-2 text-sm">
                        <div><strong>מיקום איסוף:</strong> {trip.pickupLocation}</div>
                        <div><strong>יעד:</strong> {trip.destination}</div>
                        {trip.selectedDate && <div><strong>תאריך:</strong> {formatDateDMY(trip.selectedDate)}</div>}
                        {trip.selectedTime && <div><strong>שעה:</strong> {trip.selectedTime}</div>}
                    </div>
                    <button onClick={() => onCancel(trip.tripId)} className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 px-2 rounded">בטל הזמנה</button>
                </div>
            ) : (
                <>
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-2">שם משפחה</label>
                            <input type="text" value={trip.lastName} onChange={e => onFieldChange(trip.id, { lastName: e.target.value })} placeholder="הכנס שם משפחה" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" autoComplete="family-name" />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-2">טלפון</label>
                            <input type="tel" value={trip.phone} onChange={e => onFieldChange(trip.id, { phone: e.target.value })} placeholder="הכנס מספר טלפון" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" autoComplete="tel" />
                        </div>
                    </div>
                    <div className="flex mb-6 bg-gray-700 rounded-lg p-1">
                        <button onClick={() => onFieldChange(trip.id, { activeTab: 'immediate' })} className={`flex-1 py-2 px-4 rounded-md transition-colors ${trip.activeTab === 'immediate' ? 'bg-amber-500 text-black' : 'text-gray-300 hover:text-white'}`}>נסיעה מיידית</button>
                        <button onClick={() => onFieldChange(trip.id, { activeTab: 'scheduled' })} className={`flex-1 py-2 px-4 rounded-md transition-colors ${trip.activeTab === 'scheduled' ? 'bg-amber-500 text-black' : 'text-gray-300 hover:text-white'}`}>נסיעה מתוזמנת</button>
                        <button onClick={() => onFieldChange(trip.id, { activeTab: 'recurring' })} className={`flex-1 py-2 px-4 rounded-md transition-colors ${trip.activeTab === 'recurring' ? 'bg-amber-500 text-black' : 'text-gray-300 hover:text-white'}`}>נסיעה מחזורית</button>
                    </div>

                    {trip.activeTab === 'immediate' && (
                        <div className="space-y-4">
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2">מיקום איסוף</label>
                                <input type="text" value={trip.pickupLocation} onChange={(e) => { const value = e.target.value; onFieldChange(trip.id, { pickupLocation: value, showPickupSuggestions: true }); onFetchSuggestions(value, true, trip.id); }} onBlur={() => { setTimeout(() => onFieldChange(trip.id, { showPickupSuggestions: false }), 200); }} placeholder="הכנס כתובת איסוף או השתמש במיקום הנוכחי" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" />
                                {trip.showPickupSuggestions && trip.pickupSuggestions.length > 0 && ( <div className="absolute z-10 w-full bg-gray-700 border border-gray-600 rounded-lg mt-1 max-h-48 overflow-y-auto">{trip.pickupSuggestions.map((suggestion, index) => (<button key={index} onClick={() => onSelectSuggestion(trip.id, suggestion, true)} className="w-full text-right p-3 hover:bg-gray-600 text-white border-b border-gray-600 last:border-b-0">{suggestion.description}</button>))}</div>)}
                                {currentLocation && (<button onClick={() => onUseCurrentLocation(trip.id)} className="mt-2 text-amber-400 hover:text-amber-300 text-sm">השתמש במיקום הנוכחי</button>)}
                            </div>
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2">יעד</label>
                                <input type="text" value={trip.destination} onChange={(e) => { const value = e.target.value; onFieldChange(trip.id, { destination: value, showDestinationSuggestions: true }); onFetchSuggestions(value, false, trip.id); }} onBlur={() => { setTimeout(() => onFieldChange(trip.id, { showDestinationSuggestions: false }), 200); }} placeholder="הכנס כתובת יעד" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" />
                                {trip.showDestinationSuggestions && trip.destinationSuggestions.length > 0 && ( <div className="absolute z-10 w-full bg-gray-700 border border-gray-600 rounded-lg mt-1 max-h-48 overflow-y-auto">{trip.destinationSuggestions.map((suggestion, index) => (<button key={index} onClick={() => onSelectSuggestion(trip.id, suggestion, false)} className="w-full text-right p-3 hover:bg-gray-600 text-white border-b border-gray-600 last:border-b-0">{suggestion.description}</button>))}</div>)}
                            </div>
                            <button onClick={() => onCheckAvailability(trip.id, 'immediate')} disabled={isLoading || !trip.pickupLocation || !trip.destination || waiting.immediate} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50">{waiting.immediate ? <><span role="img" aria-label="שעון חול">⏳</span> ממתין לאישור</> : (isLoading ? 'בודק זמינות...' : 'בדוק זמינות')}</button>
                        </div>
                    )}
                    {trip.activeTab === 'scheduled' && (
                         <div className="space-y-4">
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2">מיקום איסוף</label>
                                <input type="text" value={trip.pickupLocation} onChange={(e) => { const value = e.target.value; onFieldChange(trip.id, { pickupLocation: value, showPickupSuggestions: true }); onFetchSuggestions(value, true, trip.id); }} onBlur={() => { setTimeout(() => onFieldChange(trip.id, { showPickupSuggestions: false }), 200); }} placeholder="הכנס כתובת איסוף" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" />
                                {trip.showPickupSuggestions && trip.pickupSuggestions.length > 0 && ( <div className="absolute z-10 w-full bg-gray-700 border border-gray-600 rounded-lg mt-1 max-h-48 overflow-y-auto">{trip.pickupSuggestions.map((suggestion, index) => (<button key={index} onClick={() => onSelectSuggestion(trip.id, suggestion, true)} className="w-full text-right p-3 hover:bg-gray-600 text-white border-b border-gray-600 last:border-b-0">{suggestion.description}</button>))}</div>)}
                            </div>
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2">יעד</label>
                                <input type="text" value={trip.destination} onChange={(e) => { const value = e.target.value; onFieldChange(trip.id, { destination: value, showDestinationSuggestions: true }); onFetchSuggestions(value, false, trip.id); }} onBlur={() => { setTimeout(() => onFieldChange(trip.id, { showDestinationSuggestions: false }), 200); }} placeholder="הכנס כתובת יעד" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" />
                                {trip.showDestinationSuggestions && trip.destinationSuggestions.length > 0 && ( <div className="absolute z-10 w-full bg-gray-700 border border-gray-600 rounded-lg mt-1 max-h-48 overflow-y-auto">{trip.destinationSuggestions.map((suggestion, index) => (<button key={index} onClick={() => onSelectSuggestion(trip.id, suggestion, false)} className="w-full text-right p-3 hover:bg-gray-600 text-white border-b border-gray-600 last:border-b-0">{suggestion.description}</button>))}</div>)}
                            </div>
                            <div className="flex gap-4 items-end">
                                <div className="flex-1"><DatePicker label="תאריך" value={trip.selectedDate ? dayjs(trip.selectedDate) : null} onChange={v => onFieldChange(trip.id, { selectedDate: v ? v.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" slotProps={{ textField: { sx: { direction: 'ltr', textAlign: 'center' } } }}/></div>
                                <div className="flex-1"><MobileTimePicker ampm={false} label="שעה" value={dayjs(`2022-04-17T${trip.selectedTime || '00:00'}`)} onChange={v => onFieldChange(trip.id, { selectedTime: dayjs(v).format('HH:mm') })} slotProps={{textField: {sx: { direction: 'ltr', textAlign: 'center' }}}}/></div>
                            </div>
                            <button onClick={() => onCheckAvailability(trip.id, 'scheduled')} disabled={isLoading || !trip.pickupLocation || !trip.destination || !trip.selectedDate || !trip.selectedTime || waiting.scheduled} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50">{waiting.scheduled ? <><span role="img" aria-label="שעון חול">⏳</span> ממתין לאישור</> : (isLoading ? 'בודק זמינות...' : 'בדוק זמינות')}</button>
                        </div>
                    )}
                    {trip.activeTab === 'recurring' && (
                        <div className="space-y-4">
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2">מיקום איסוף</label>
                                <input type="text" value={trip.pickupLocation} onChange={(e) => { const value = e.target.value; onFieldChange(trip.id, { pickupLocation: value, showPickupSuggestions: true }); onFetchSuggestions(value, true, trip.id); }} onBlur={() => { setTimeout(() => onFieldChange(trip.id, { showPickupSuggestions: false }), 200); }} placeholder="הכנס כתובת איסוף" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" />
                                {trip.showPickupSuggestions && trip.pickupSuggestions.length > 0 && ( <div className="absolute z-10 w-full bg-gray-700 border border-gray-600 rounded-lg mt-1 max-h-48 overflow-y-auto">{trip.pickupSuggestions.map((suggestion, index) => (<button key={index} onClick={() => onSelectSuggestion(trip.id, suggestion, true)} className="w-full text-right p-3 hover:bg-gray-600 text-white border-b border-gray-600 last:border-b-0">{suggestion.description}</button>))}</div>)}
                            </div>
                            <div className="relative">
                                <label className="block text-sm font-medium mb-2">יעד</label>
                                <input type="text" value={trip.destination} onChange={(e) => { const value = e.target.value; onFieldChange(trip.id, { destination: value, showDestinationSuggestions: true }); onFetchSuggestions(value, false, trip.id); }} onBlur={() => { setTimeout(() => onFieldChange(trip.id, { showDestinationSuggestions: false }), 200); }} placeholder="הכנס כתובת יעד" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" />
                                {trip.showDestinationSuggestions && trip.destinationSuggestions.length > 0 && ( <div className="absolute z-10 w-full bg-gray-700 border border-gray-600 rounded-lg mt-1 max-h-48 overflow-y-auto">{trip.destinationSuggestions.map((suggestion, index) => (<button key={index} onClick={() => onSelectSuggestion(trip.id, suggestion, false)} className="w-full text-right p-3 hover:bg-gray-600 text-white border-b border-gray-600 last:border-b-0">{suggestion.description}</button>))}</div>)}
                            </div>
                            <div className="flex gap-4 items-end">
                                <div className="flex-1"><DatePicker label="תאריך" value={trip.selectedDate ? dayjs(trip.selectedDate) : null} onChange={v => onFieldChange(trip.id, { selectedDate: v ? v.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" slotProps={{ textField: { sx: { direction: 'ltr', textAlign: 'center' } } }}/></div>
                                <div className="flex-1"><MobileTimePicker ampm={false} label="שעה" value={dayjs(`2022-04-17T${trip.selectedTime || '00:00'}`)} onChange={v => onFieldChange(trip.id, { selectedTime: dayjs(v).format('HH:mm') })} slotProps={{textField: {sx: { direction: 'ltr', textAlign: 'center' }}}}/></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">מחזוריות</label>
                                    <select value={trip.recurrenceType} onChange={(e) => onFieldChange(trip.id, { recurrenceType: e.target.value })} className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none"><option value="daily">כל יום</option><option value="weekly">כל שבוע</option><option value="monthly">כל חודש</option></select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">מספר פעמים</label>
                                    <input type="number" value={trip.recurrenceCount} onChange={(e) => onFieldChange(trip.id, { recurrenceCount: parseInt(e.target.value) || 1 })} min="1" max="30" className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none" />
                                </div>
                            </div>
                            <button onClick={() => onCheckAvailability(trip.id, 'recurring')} disabled={isLoading || !trip.pickupLocation || !trip.destination || !trip.selectedDate || !trip.selectedTime || waiting.recurring} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50">{waiting.recurring ? <><span role="img" aria-label="שעון חול">⏳</span> ממתין לאישור</> : (isLoading ? 'בודק זמינות...' : 'בדוק זמינות')}</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default function Monit({ effectiveUserId }) {
    const [trips, setTrips] = useState([{ id: 'trip-1', lastName: localStorage.getItem('monit_lastName') || '', phone: localStorage.getItem('monit_phone') || '', activeTab: 'immediate', pickupLocation: '', destination: '',
selectedDate: dayjs().format('YYYY-MM-DD'),
    selectedTime: dayjs().format('HH:mm'),
 recurrenceType: 'daily', 
recurrenceCount: 1, status: null, tripId: null, waitingForApproval: { immediate: false, scheduled: false, recurring: false }, pickupSuggestions: [], destinationSuggestions: [], showPickupSuggestions: false, showDestinationSuggestions: false }]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const [googleMaps, setGoogleMaps] = useState(null);

    const updateTrip = useCallback((tripId, updates) => {
        setTrips(prevTrips => prevTrips.map(trip => trip.id === tripId ? { ...trip, ...updates } : trip));
    }, []);

const addNewTrip = useCallback(() => {
    const newTrip = { 
        id: `trip-${Date.now()}`, 
        lastName: localStorage.getItem('monit_lastName') || '', 
        phone: localStorage.getItem('monit_phone') || '', 
        activeTab: 'immediate', 
        pickupLocation: '', 
        destination: '', 
        selectedDate: dayjs().format('YYYY-MM-DD'), 
        selectedTime: dayjs().format('HH:mm'), 
        recurrenceType: 'daily', 
        recurrenceCount: 1, 
        status: null, 
        tripId: null, 
        waitingForApproval: { immediate: false, scheduled: false, recurring: false }, 
        pickupSuggestions: [], 
        destinationSuggestions: [], 
        showPickupSuggestions: false, 
        showDestinationSuggestions: false 
    };
    // התיקון: מוסיפים את הנסיעה החדשה למערך הקיים במקום לדרוס אותו
    setTrips(prevTrips => [...prevTrips, newTrip]); 
}, []);

    useEffect(() => {
        if (!trips.length) { addNewTrip(); }
    }, [trips, addNewTrip]);
    
    useEffect(() => {
        if (trips.length > 0) {
            localStorage.setItem('monit_lastName', trips[0].lastName);
            localStorage.setItem('monit_phone', trips[0].phone);
        }
    }, [trips]);

    useEffect(() => {
        const apiKey = process.env.REACT_APP_Maps_API_KEY;
        if (apiKey && apiKey !== 'YOUR_API_KEY_HERE') {
            const loader = new Loader({ apiKey, version: 'weekly', libraries: ['places', 'geometry'] });
            loader.load().then(setGoogleMaps).catch(e => console.error("Failed to load Google Maps", e));
        }
        setGoogleMapsLoaded(true);
    }, []);



useEffect(() => {
    // פונקציה שתפקידה לטעון הזמנות קיימות מהשרת
    const fetchExistingTrips = async () => {
        if (!effectiveUserId) return;

        const tripsRef = collection(db, 'users', effectiveUserId, 'trips');
        // נבקש רק הזמנות שהן מאושרות או ממתינות
        const q = query(tripsRef, where("status", "in", ["pending", "confirmed"]));

        try {
            const querySnapshot = await getDocs(q);
            const existingTrips = querySnapshot.docs.map(doc => {
                const data = doc.data();
                // נמפה את המידע מהשרת למבנה של ה-state המקומי
                return {
                    id: `trip-${doc.id}`,
                    tripId: doc.id,
                    status: data.status,
                    lastName: data.customerName || '',
                    phone: data.customerPhone || '',
                    pickupLocation: data.pickupLocation,
                    destination: data.destination,
                    selectedDate: data.scheduledDate,
                    selectedTime: data.scheduledTime,
                    activeTab: 'immediate',
                    recurrenceType: 'daily',
                    recurrenceCount: 1,
                    waitingForApproval: { immediate: false, scheduled: false, recurring: false },
                    pickupSuggestions: [],
                    destinationSuggestions: [],
                    showPickupSuggestions: false,
                    showDestinationSuggestions: false
                };
            });

            if (existingTrips.length > 0) {
                setTrips(existingTrips);
            }
        } catch (error) {
            console.error("Error fetching existing trips:", error);
        }
    };

    fetchExistingTrips();
    // ה-useEffect הזה ירוץ פעם אחת כשהקומפוננטה עולה
}, [effectiveUserId]);

    useEffect(() => {
        navigator.geolocation?.getCurrentPosition(
            (position) => setCurrentLocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
            (error) => console.error('Error getting location:', error)
        );
    }, []);

useEffect(() => {
    // מצא את כל הנסיעות שנשלחו ל-Firebase אך עדיין לא קיבלו סטטוס סופי
    const pendingTrips = trips.filter(trip => trip.tripId && trip.status !== 'confirmed' && trip.status !== 'cancelled');

    if (pendingTrips.length === 0 || !effectiveUserId) {
        return; // אם אין למה להאזין, צא
    }

    // צור מאזין נפרד לכל נסיעה שממתינה
    const unsubscribers = pendingTrips.map(tripToWatch => {
        const tripDocRef = doc(db, 'users', effectiveUserId, 'trips', tripToWatch.tripId);
        // onSnapshot מחזיר פונקציית ביטול שאותה נשמור
        return onSnapshot(tripDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                // עדכן את הסטטוס של הנסיעה הספציפית שהשתנתה
                updateTrip(tripToWatch.id, { 
                    status: data.status,
                    waitingForApproval: data.status === 'pending' 
                        ? tripToWatch.waitingForApproval 
                        : { immediate: false, scheduled: false, recurring: false }
                });
            } else {
                // התיקון: אם המסמך נמחק (בוטל ע"י הנהג), הסר אותו מהתצוגה
                setTrips(prevTrips => prevTrips.filter(t => t.id !== tripToWatch.id));
            }
        });
    });

    // פונקציית הניקוי תבטל את כל המאזינים שיצרנו
    return () => {
        unsubscribers.forEach(unsub => unsub());
    };
}, [trips, effectiveUserId, updateTrip, setTrips]);

    const getPlaceSuggestions = useCallback((inputValue, isPickup, tripId) => {
        if (!googleMaps?.maps || !inputValue || inputValue.length < 2) return;
        const service = new googleMaps.maps.places.AutocompleteService();
        service.getPlacePredictions({ input: inputValue, componentRestrictions: { country: 'il' } }, (predictions) => {
            const update = isPickup ? { pickupSuggestions: predictions || [] } : { destinationSuggestions: predictions || [] };
            updateTrip(tripId, update);
        });
    }, [googleMaps, updateTrip]);
    
    const reverseGeocode = useCallback(async (lat, lng) => {
        if (!googleMaps?.maps) return 'מיקום לא ידוע';
        const geocoder = new googleMaps.maps.Geocoder();
        return new Promise((resolve) => {
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                resolve(status === 'OK' && results[0] ? results[0].formatted_address : 'שגיאה באיתור כתובת');
            });
        });
    }, [googleMaps]);

    const selectPlace = (place, isPickup, tripId) => {
        const fieldToUpdate = isPickup ? 'pickupLocation' : 'destination';
        const suggestionsToClear = isPickup ? 'showPickupSuggestions' : 'showDestinationSuggestions';
        updateTrip(tripId, { [fieldToUpdate]: place.description, [suggestionsToClear]: false });
    };

    const calculateTripDetails = async (origin, destination) => {
        if (!googleMaps?.maps) return { distance: 'N/A', duration: 'N/A' };
        return new Promise((resolve) => {
            const directionsService = new googleMaps.maps.DirectionsService();
            directionsService.route({ origin, destination, travelMode: googleMaps.maps.TravelMode.DRIVING }, (result, status) => {
                if (status === 'OK') {
                    const leg = result.routes[0].legs[0];
                    resolve({ distance: leg.distance.text, duration: leg.duration.text, durationMinutes: Math.ceil(leg.duration.value / 60) });
                } else {
                    resolve({ distance: 'N/A', duration: 'N/A' });
                }
            });
        });
    };

    const checkDriverAvailabilityInFirebase = async (driverId, requestedDate, requestedTime) => {
        if (!driverId || !requestedDate || !requestedTime) return false;
        const tripsRef = collection(db, 'users', driverId, 'trips');
        const requestedHour = parseInt(requestedTime.split(':')[0], 10);
        const q = query(tripsRef, where("scheduledDate", "==", requestedDate), where("status", "in", ["pending", "confirmed"]));
        try {
            const querySnapshot = await getDocs(q);
            return !querySnapshot.docs.some(doc => parseInt(doc.data().scheduledTime?.split(':')[0], 10) === requestedHour);
        } catch (error) {
            console.error("Error checking availability:", error);
            return false;
        }
    };
    
const cancelTrip = async (tripIdToCancel) => {
    if (!effectiveUserId || !tripIdToCancel) return;
    const tripRef = doc(db, 'users', effectiveUserId, 'trips', tripIdToCancel);
    try {
        // גם הנוסע מוחק את ההזמנה
        await deleteDoc(tripRef);
        alert("ההזמנה בוטלה");
        // התיקון: מסירים את ההזמנה מהתצוגה המקומית
        setTrips(prevTrips => prevTrips.filter(t => t.tripId !== tripIdToCancel));
    } catch (error) {
        console.error("Error cancelling trip:", error);
        alert("שגיאה בביטול ההזמנה.");
    }
};
    const handleTripRequest = async (tripId, type) => {
        setIsLoading(true);
        const trip = trips.find(t => t.id === tripId);
        if (!trip) { setIsLoading(false); return; }

        const dateToCheck = type === 'immediate' ? dayjs().format('YYYY-MM-DD') : trip.selectedDate;
        const timeToCheck = type === 'immediate' ? dayjs().format('HH:mm') : trip.selectedTime;

        const isAvailable = await checkDriverAvailabilityInFirebase(effectiveUserId, dateToCheck, timeToCheck);
        if (isAvailable) {
            const tripDetails = await calculateTripDetails(trip.pickupLocation, trip.destination);
            const tripData = { type, pickupLocation: trip.pickupLocation, destination: trip.destination, scheduledDate: dateToCheck, scheduledTime: timeToCheck, tripDetails, status: 'pending', createdAt: Timestamp.now(), customerName: trip.lastName, customerPhone: trip.phone, duration: tripDetails.durationMinutes || 60, recurrenceType: trip.recurrenceType, recurrenceCount: trip.recurrenceCount };
            
            const docRef = await addDoc(collection(db, 'users', effectiveUserId, 'trips'), tripData);
            updateTrip(tripId, { tripId: docRef.id, waitingForApproval: { ...trip.waitingForApproval, [type]: true } });
            alert('הבקשה נשלחה לנהג!');
        } else {
            alert('הנהג אינו פנוי במועד המבוקש. אנא בחר מועד אחר.');
        }
        setIsLoading(false);
    };

    return (
        <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="max-w-4xl mx-auto bg-gray-800 text-white p-6 rounded-xl">
                        <h2 className="text-3xl font-bold mb-6 text-amber-400 text-center">הזמנת מונית</h2>
                        {trips.map((trip) => (
                            <TripBookingBlock
                                key={trip.id}
                                trip={trip}
                                onFieldChange={updateTrip}
                                onCheckAvailability={handleTripRequest}
                                isLoading={isLoading}
                                onCancel={cancelTrip}
                                isConfirmed={trip.status === 'confirmed'}
                                onUseCurrentLocation={async (tripId) => { if (currentLocation) { const address = await reverseGeocode(currentLocation.lat, currentLocation.lng); updateTrip(tripId, { pickupLocation: address }); } }}
                                onSelectSuggestion={selectPlace}
                                onFetchSuggestions={getPlaceSuggestions}
                                currentLocation={currentLocation}
                            />
                        ))}
                        {trips.some(trip => trip.status === 'confirmed') && (
                            <div className="text-center mt-6">
                                <button onClick={addNewTrip} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold transition-colors">הוסף נסיעה חדשה</button>
                            </div>
                        )}
                    </div>
                </LocalizationProvider>
            </ThemeProvider>
        </CacheProvider>
    );
}